<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  createThemeQueryGenerationRequest,
  ErrorResponse,
  replaceThemeQueryGenerationRequest,
  updateThemeQueryGenerationRequest,
} from '@/api/themeQueryGenerationRequest';
import { DgrDialog, DgrIcon } from '@stockmarkteam/donguri-ui';
import SkeletonSentence from '@/components/common/atoms/skeleton-sentence.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import InputEditor from '@/components/theme/edit/theme-setting-support-modal/input-editor.vue';
import PreviewQuery from '@/components/theme/edit/theme-setting-support-modal/preview-query.vue';
import { useThemeSettingSupportModal } from '@/components/theme/edit/theme-setting-support-modal/useThemeSettingSupportModal';
import { ThemeQueryGenerationRequestResponse } from '@/types/themeQueryGenerationRequest';
import { STATES, useThemeQueryGenerationRequestById } from '@/utils/swr';
import { STREAMING_DATA_TYPES } from '@/utils/themeSettingSupport/constants';
import {
  ERROR_DESC_MODIFY_REQUEST,
  ERROR_DESC_RETRY_LATER,
  ERROR_MSG_FAILED_TO_GENERATE_RESPONSE,
  ERROR_MSG_UNABLE_TO_GENERATE_RESPONSE,
} from '@/utils/themeSettingSupport/constants';

const isOpen = defineModel<boolean>('isOpen', {
  required: true,
});
// refの初期値
const INITIAL_MODE = 'input';
const INITIAL_IS_LOADING = false;
const INITIAL_THEME_QUERY_GENERATION_REQUEST_ID = null;
const INITIAL_INPUT_TEXT = '';

// ダイアログの状態 (input: 依頼文入力, query: クエリ表示)
const mode = ref<'input' | 'previewQuery'>(INITIAL_MODE);
const isLoading = ref<boolean>(INITIAL_IS_LOADING);
const themeQueryGenerationRequestId = ref<number | null>(
  INITIAL_THEME_QUERY_GENERATION_REQUEST_ID,
);
const inputText = ref(INITIAL_INPUT_TEXT);
const previewQuery = ref<InstanceType<typeof PreviewQuery> | null>(null);

const enableSubmitButton = computed(() => {
  return inputText.value.length > 0;
});

const { trackAIKeywordsRequest } = useThemeSettingSupportModal();
const { createSnackbar } = useSnackbar();
const {
  data: themeQueryGenerationRequest,
  state: stateThemeQueryGenerationRequestById,
  mutate: mutateThemeQueryGenerationRequestById,
} = useThemeQueryGenerationRequestById(themeQueryGenerationRequestId);

const isPreviewingQuery = computed(
  () => mode.value === 'previewQuery' && !isLoading.value,
);
const isInputtingRequestText = computed(
  () => mode.value === 'input' && !isLoading.value,
);

watch(stateThemeQueryGenerationRequestById, () => {
  if (stateThemeQueryGenerationRequestById.value === STATES.ERROR) {
    createSnackbar({
      message: 'クエリ詳細の取得に失敗しました',
      type: 'error',
    });
  } else if (
    stateThemeQueryGenerationRequestById.value === STATES.PENDING ||
    stateThemeQueryGenerationRequestById.value === STATES.VALIDATING
  ) {
    isLoading.value = true;
  } else if (stateThemeQueryGenerationRequestById.value === STATES.SUCCESS) {
    isLoading.value = false;
  }
});

const resetToInitialState = () => {
  mode.value = INITIAL_MODE;
  isLoading.value = INITIAL_IS_LOADING;
  themeQueryGenerationRequestId.value =
    INITIAL_THEME_QUERY_GENERATION_REQUEST_ID;
  inputText.value = INITIAL_INPUT_TEXT;
};

const handleErrorResponse = (
  errorResponse: Omit<ErrorResponse, 'isSuccess'>,
) => {
  createSnackbar({
    message: errorResponse.message,
    description: errorResponse.description,
    type: 'error',
  });
  closeModal();
};

const handleChunk = async (chunk: ThemeQueryGenerationRequestResponse) => {
  switch (chunk.type) {
    case STREAMING_DATA_TYPES.ERROR:
      handleErrorResponse({
        message: ERROR_MSG_UNABLE_TO_GENERATE_RESPONSE,
        description: ERROR_DESC_RETRY_LATER,
      });
      break;
    case STREAMING_DATA_TYPES.RESULT:
      await trackAIKeywordsRequest(chunk.data);
      themeQueryGenerationRequestId.value = chunk.data.id;
      mode.value = 'previewQuery';
      break;
    case STREAMING_DATA_TYPES.KEYWORDS_NOT_EXTRACTED:
      handleErrorResponse({
        message: ERROR_MSG_FAILED_TO_GENERATE_RESPONSE,
        description: ERROR_DESC_MODIFY_REQUEST,
      });
      break;
  }
};

const generateQuery = async () => {
  isLoading.value = true;
  for await (const chunk of createThemeQueryGenerationRequest(
    inputText.value,
  )) {
    handleChunk(chunk);
  }
};
const handleDialogClick = () => {
  // プレビュー表示中にダイアログ内をクリックした場合はpopupを閉じる
  if (previewQuery.value) {
    previewQuery.value.closePopup();
  }
};
const closeModal = () => {
  isOpen.value = false;
  resetToInitialState();
};
const editRequestTextHandler = async (requestText: string) => {
  if (!themeQueryGenerationRequest.value) return;
  isLoading.value = true;
  for await (const chunk of replaceThemeQueryGenerationRequest({
    id: themeQueryGenerationRequest.value.id,
    requestText,
  })) {
    handleChunk(chunk);
  }
};
const closeInterceptorHandler = () => {
  resetToInitialState();
  return true;
};
const deleteKeywordHandler = async (uuid: string, lang: 'ja' | 'en') => {
  if (!themeQueryGenerationRequest.value) return;
  const result = await updateThemeQueryGenerationRequest({
    id: themeQueryGenerationRequest.value.id,
    deletedKeywords: [
      ...(themeQueryGenerationRequest.value.deleted_keywords || []),
      { uuid, lang },
    ],
  });
  if (result.isSuccess) {
    mutateThemeQueryGenerationRequestById(() => result.data);
  } else {
    createSnackbar({
      message: result.message,
      description: result.description,
      type: 'error',
    });
    closeModal();
  }
};
const restoreKeywordHandler = async (uuid: string, lang: 'ja' | 'en') => {
  if (!themeQueryGenerationRequest.value) return;
  const result = await updateThemeQueryGenerationRequest({
    id: themeQueryGenerationRequest.value.id,
    deletedKeywords: (
      themeQueryGenerationRequest.value.deleted_keywords || []
    ).filter(keyword => keyword.uuid !== uuid || keyword.lang !== lang),
  });
  if (result.isSuccess) {
    mutateThemeQueryGenerationRequestById(() => result.data);
  } else {
    createSnackbar({
      message: result.message,
      description: result.description,
      type: 'error',
    });
    closeModal();
  }
};
const selectRequestHandler = (selectedItemId: number) => {
  themeQueryGenerationRequestId.value = selectedItemId;
  mode.value = 'previewQuery';
};
</script>

<template>
  <DgrDialog
    v-model:open="isOpen"
    :with-header-divider="false"
    :modal="false"
    :close-interceptor-handler="closeInterceptorHandler"
    :has-footer="isInputtingRequestText"
  >
    <template #description>
      <div class="modal-header">
        <div class="title c-text-regular--m">
          <span>AIに検索クエリの作成を依頼</span>

          <VTooltip placement="right">
            <DgrIcon name="question-circle" size="small" />
            <template #popper>
              入力例
              <ul class="tooltip-list">
                <li>パワー半導体について知りたい</li>
                <li>ペロブスカイト太陽電池の技術動向について調べたい</li>
                <li>半導体 サプライチェーン</li>
                <li>バイオ医薬品の法規制</li>
                <li>生成AIの活用事例</li>
              </ul>
            </template>
          </VTooltip>
        </div>

        <div>
          <button @click.capture="isOpen = false" class="close-button">
            <DgrIcon name="times" alt="close button" size="small" />
          </button>
        </div>
      </div>

      <hr class="divider" />

      <div
        @click="handleDialogClick"
        class="modal-body"
        :class="{ previewQuery: isPreviewingQuery }"
      >
        <template v-if="isLoading">
          <SkeletonSentence :number-of-lines="3" />
        </template>
        <template v-else-if="mode === 'input'">
          <InputEditor
            v-model="inputText"
            @select-history="selectRequestHandler"
            @submit="generateQuery"
          />
        </template>
        <template
          v-else-if="mode === 'previewQuery' && themeQueryGenerationRequest"
        >
          <PreviewQuery
            :theme-query-generation-request="themeQueryGenerationRequest"
            @close-modal="closeModal"
            @edit-request-text="editRequestTextHandler"
            @delete-keyword="deleteKeywordHandler"
            @restore-keyword="restoreKeywordHandler"
            ref="previewQuery"
          />
        </template>
      </div>
    </template>
    <template #footer>
      <div class="submit-section">
        <button
          class="c-btn--AnewsPrimary"
          :class="{
            disabled: !enableSubmitButton,
          }"
          :disabled="!enableSubmitButton"
          @click="generateQuery"
        >
          <DgrIcon
            size="small"
            :keep-fill="false"
            name="sparkles-fill"
            class="ai-icon"
          />依頼
        </button>
      </div>
    </template>
  </DgrDialog>
</template>

<style lang="scss" scoped>
.modal-header {
  flex: 0 0 14px;
  height: 14px;
  margin: -16px;
  margin-bottom: 0;
  padding: 16px 0 16px 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.title {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
}

.tooltip-list {
  padding-left: 12px;
}

.close-button {
  background-color: $color-white;
  border: none;
  padding: 8px 12px;
  margin-right: 4px;

  &:hover {
    background: $color-gray200;
  }
}

.divider {
  width: calc(100% + 32px);
  height: 0;
  margin: 0 -16px;
  border: 0;
  border-top: 1px solid $color-gray400;
}

.modal-body {
  width: 980px;
  &.previewQuery {
    height: 756px;
  }
}

.submit-section {
  button {
    padding: 8px 16px;
    cursor: pointer;
    align-self: flex-end;
    flex-shrink: 0;
  }
  .ai-icon {
    margin-right: 3px;
  }
}
</style>
